// SZEMÉLY-KÖTŐDÉS CELLA — a lista-oldalak közös „több személy egy cellában" megjelenítője: avatar + locale-tudatos
// név (`PersonChip size="sm"`), 2 látszik, a többi legördülőben (`ExpandableCell`), üres → halvány em-dash.
//
// MIÉRT KÖZÖS: a fiók-lista (Ügyféladminisztrátor + Kapcsolattartó oszlop) és a kapcsolattartó-lista
// (Ügyféladminisztrátorok oszlop) BÁJTRA ugyanezt a cellát rajzolja. A promóció-szabály szerint a 2. helyen már
// nem másolat, hanem közös komponens — így a „2 látszik / 3-tól legördül" szabály és az avatar-méret EGY helyen
// dől el (egy oldalon módosítva a másik nem csúszik el).
//
// A `moreLabel`/`triggerAriaLabel` szándékosan PROP: a feliratok modul-specifikus i18n-kulcsokból jönnek
// (`accounts.more` / `contacts.more`), a komponens nem tudhatja, melyik lista rendereli.
import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpandableCell } from './expandable-cell';
import { PersonChip } from './person-chip';

/** Egy személy-referencia (a contract `AccountPersonRefSchema`-jának megjelenítéshez szükséges része). */
export interface PersonRefCellItem {
  id: string;
  /** `null` = az identity nem oldható fel (törölt személy) → „Törölt felhasználó" címke, sosem nyers UUID. */
  name: string | null;
  givenName: string | null;
  familyName: string | null;
  avatarUrl: string | null;
  /** Részleg-szín token (belső kolléga) — az avatar háttere; null → semleges. */
  teamColor: string | null;
}

export interface PersonRefCellProps {
  items: PersonRefCellItem[];
  /** „+N további" felirat a legördülő trigger-en (modul-specifikus i18n-kulcsból). */
  moreLabel: (count: number) => string;
  /** A trigger aria-labelje (modul-specifikus i18n-kulcsból). */
  triggerAriaLabel: string;
}

export function PersonRefCell({
  items,
  moreLabel,
  triggerAriaLabel,
}: PersonRefCellProps): JSX.Element {
  const { t } = useTranslation();
  if (items.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <ExpandableCell
      items={items}
      getKey={(x) => x.id}
      visible={2}
      threshold={3}
      moreLabel={moreLabel}
      triggerAriaLabel={triggerAriaLabel}
      renderItem={(x) => (
        <PersonChip
          size="sm"
          givenName={x.givenName}
          familyName={x.familyName}
          name={x.name ?? t('common.deletedUser')}
          avatarUrl={x.avatarUrl}
          accentColor={x.teamColor}
        />
      )}
    />
  );
}
